import { z } from 'zod';
import { CombinationKind, ExerciseTypesEnum, MediaTypeEnum } from './trainingmodel';
import { CompletionEnum } from './traininghistorymodel';
// NOTE: media/completion literals use z.literal(MediaTypeEnum.x) / z.nativeEnum so the
// parsed OUTPUT types are the enum members themselves — a bare z.literal('image') parses
// to the string literal "image", which TypeScript refuses to assign to MediaTypeEnum.image.

// Zod guards for the v3 exercise-combination shapes (data design of record:
// new-features/exercise_combination_data_design_v3_1.md §2 structural rules).
// These are the discriminated unions at the read/write seams: a blob that CLAIMS
// version 3 must pass them or be refused — never half-parsed. The v2→v3 read-time
// wrap builds its output structurally (solo wraps always satisfy these rules),
// so parsing only gates genuine v3 payloads.

export const PhaseColorZod = z.enum(['blue', 'green', 'yellow', 'red', 'plain']);

const PauseTuple = z.tuple([z.number(), z.number()]).optional();

const SeriesModelZod = z
  .object({
    type: z.enum(['single', 'multiple', 'error']),
    desc: z.string(),
    unit: z.enum(['s', 'min']).optional(),
    noSeries: z.number(),
    noRepetitions: z.tuple([z.number().optional(), z.number()]),
  })
  .passthrough();

const MediaZod = z.discriminatedUnion('type', [
  z.object({ type: z.literal(MediaTypeEnum.image), src: z.string().optional(), mediaUUID: z.string() }),
  z.object({ type: z.literal(MediaTypeEnum.video), src: z.string().optional(), mediaUUID: z.string() }),
  z.object({ type: z.literal(MediaTypeEnum.youtube), mediaUUID: z.string() }),
]);

const ExecutionModelv3Zod = z
  .object({
    series: z.array(SeriesModelZod),
    pause: PauseTuple,
  })
  .passthrough();

export const SlotModelZod = z
  .object({
    exercise: z.string(),
    type: z.nativeEnum(ExerciseTypesEnum),
    media: z.array(MediaZod),
    execution: ExecutionModelv3Zod,
    cadence: z.string().optional(),
    observations: z.string().optional(),
  })
  .passthrough();

// Combination structural invariants (§2): superset|complex ⇒ every slot has exactly
// ONE SeriesModel whose noSeries equals the block's rounds (all moves are done the
// same number of times; reps may differ per slot). Solo ⇒ exactly one slot, ramps
// allowed, rounds ignored. Applied over the parsed union value (superRefine on a
// ZodObject returns ZodEffects, which is not a valid discriminatedUnion member).
const combinationInvariant = (
  block: { kind: CombinationKind; execution: { rounds: number }; slots: { execution: { series: { noSeries: number }[] } }[] },
  ctx: z.RefinementCtx,
) => {
  if (block.kind === CombinationKind.solo) {
    return;
  }
  for (const [i, slot] of block.slots.entries()) {
    if (slot.execution.series.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${block.kind} slot ${i}: expected exactly 1 SeriesModel, got ${slot.execution.series.length}`,
      });
    }
  }
  const first = block.slots[0];
  if (first && first.execution.series[0] && first.execution.series[0].noSeries !== block.execution.rounds) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${block.kind}: slot A noSeries must equal rounds (${block.execution.rounds})`,
    });
  }
};

const BlockExecutionZod = z.object({
  rounds: z.number(),
  roundPause: PauseTuple,
});

const blockFields = {
  name: z.string().optional(),
  instructions: z.string().optional(),
  sharedLoad: z.boolean().optional(),
  execution: BlockExecutionZod,
};

const BlockModelBaseZod = z.discriminatedUnion('kind', [
  z
    .object({
      ...blockFields,
      kind: z.literal(CombinationKind.solo),
      slots: z.array(SlotModelZod).length(1),
    })
    .passthrough(),
  z
    .object({
      ...blockFields,
      kind: z.literal(CombinationKind.superset),
      slots: z.array(SlotModelZod).min(2),
    })
    .passthrough(),
  z
    .object({
      ...blockFields,
      kind: z.literal(CombinationKind.complex),
      slots: z.array(SlotModelZod).min(2),
    })
    .passthrough(),
]);

export const BlockModelZod = BlockModelBaseZod.superRefine(combinationInvariant);

export const PhaseModelZod = z
  .object({
    name: z.string(),
    color: PhaseColorZod.optional(),
    blocks: z.array(BlockModelZod),
  })
  .passthrough();

export const TrainingDayModelv3Zod = z
  .object({
    name: z.string(),
    phases: z.array(PhaseModelZod),
  })
  .passthrough();

export const WorkoutModelDBv3Zod = z
  .object({
    version: z.literal(3),
    planUUID: z.string(),
    userUUID: z.string().optional(),
    workoutName: z.string(),
    objective: z.string(),
    startDate: z.date(),
    endDate: z.date(),
    published: z.date().optional(),
    workouts: z.array(TrainingDayModelv3Zod),
  })
  .passthrough();

// ---------------- log side ----------------

const CompletionTypeZod = z
  .object({
    weight: z.number().optional(),
    completion: z.nativeEnum(CompletionEnum),
    note: z.string(),
  })
  .passthrough();

const ExecutionModelv2Zod = z
  .object({
    series: z.array(SeriesModelZod),
    pause: PauseTuple,
    cadence: z.tuple([z.number(), z.number()]).optional(),
  })
  .passthrough();

export const ExerciseLogEntryv2Zod = z
  .object({
    exercise: z.string(),
    execution: ExecutionModelv2Zod,
    type: z.nativeEnum(ExerciseTypesEnum),
    completion: z.array(CompletionTypeZod),
    notes: z.string().optional(),
  })
  .passthrough();

export const BlockLogEntryZod = z
  .object({
    kind: z.nativeEnum(CombinationKind),
    slots: z.array(ExerciseLogEntryv2Zod).min(1),
    notes: z.string().optional(),
  })
  .passthrough();

export const TrainingDayLogv3Zod = z
  .object({
    version: z.literal(3),
    plansUUID: z.string(),
    workoutName: z.string(),
    date: z.date(),
    log: z.array(BlockLogEntryZod),
  })
  .passthrough();
